import { useState, useMemo, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Plus, Repeat2 } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CalendarEvent {
  id: number | string;
  title: string;
  startTimeUtc: number; // ms epoch
  endTimeUtc: number; // ms epoch
  eventType?: string;
  joinLink?: string;
  description?: string;
  isPublished?: boolean;
  color?: string;
  recurrenceRule?: string; // "none" | "daily" | "weekly" | "monthly"
  // Recurrence occurrence metadata (set by @shared/bc360CalendarRecurrence
  // expansion) — consumers must use these, never parse `id`.
  seriesEventId?: number;
  occurrenceStartTimeUtc?: number;
  isRecurrenceOccurrence?: boolean;
}

export interface WeeklyCalendarViewProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onSlotClick?: (startMs: number) => void;
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HOUR_START = 0;  // Device-local meetings can fall outside US business hours.
const HOUR_END = 24;
const HOUR_HEIGHT = 52; // px per hour — comfortable reading height
const MIN_EVENT_HEIGHT = 24; // px minimum event block height
const SCROLL_TO_HOUR = 9; // auto-scroll to 9 AM on mount
const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// Side-by-side event chips shrink linearly with the overlap count and have no
// width floor — 3+ same-time events used to squeeze into unreadable slivers
// (client-reported 2026-09-02). Cap side-by-side chips at 2 and collapse any
// remainder into a single "+N more" chip instead of shrinking further.
const MAX_VISIBLE_COLS = 2;

const EVENT_TYPE_COLORS: Record<string, string> = {
  Training: "#84ab4a",
  Webinar: "#1A6FD4",
  Workshop: "#E8620A",
  Meeting: "#C8920A",
};
const DEFAULT_EVENT_COLOR = "#7B4FD4";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Return the Monday of the week containing `date` (local time). */
function getMondayOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Add `n` days to `date` (local). */
function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** Format a Date as "Mar 27" */
function formatDayLabel(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Format a Date as "YYYY-MM-DD" for keying */
function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** True if two dates share the same local calendar day */
function isSameDay(a: Date, b: Date): boolean {
  return toDateKey(a) === toDateKey(b);
}

/** True if the given date is today (local) */
function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

/** Get event color from type or override prop */
function resolveColor(event: CalendarEvent): string {
  if (event.color) return event.color;
  if (event.eventType && EVENT_TYPE_COLORS[event.eventType]) {
    return EVENT_TYPE_COLORS[event.eventType];
  }
  return DEFAULT_EVENT_COLOR;
}

/** Hex color -> rgba string with alpha */
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export interface LayoutEventItem {
  kind: "event";
  event: CalendarEvent;
  colIndex: number;
  colTotal: number;
  top: number;
  height: number;
}

export interface LayoutMoreItem {
  kind: "more";
  events: CalendarEvent[];
  colIndex: number;
  colTotal: number;
  top: number;
  height: number;
}

export type LayoutItem = LayoutEventItem | LayoutMoreItem;

/** Time-based top/height (px) for one event, clamped to the visible hour range. */
function computeEventTimeBox(ev: CalendarEvent): { top: number; height: number } {
  const startDate = new Date(ev.startTimeUtc);
  const endDate = new Date(ev.endTimeUtc);

  const startHour = startDate.getHours() + startDate.getMinutes() / 60;
  const endHour = endDate.getHours() + endDate.getMinutes() / 60;

  const clampedStart = Math.max(startHour, HOUR_START);
  const clampedEnd = Math.min(endHour, HOUR_END);

  const top = (clampedStart - HOUR_START) * HOUR_HEIGHT;
  const durationMinutes = Math.max((clampedEnd - clampedStart) * 60, 0);
  const height = Math.max(durationMinutes * (HOUR_HEIGHT / 60), MIN_EVENT_HEIGHT);

  return { top, height };
}

/**
 * Given a list of events for a single day, compute overlap groups and assign
 * column index / total for each event so they can be rendered side-by-side.
 * A group larger than MAX_VISIBLE_COLS renders only MAX_VISIBLE_COLS - 1
 * events at full readable width, plus one "+N more" chip covering the rest —
 * rather than shrinking every chip's width linearly with no floor.
 */
export function computeLayout(events: CalendarEvent[]): LayoutItem[] {
  // Sort by start time
  const sorted = [...events].sort((a, b) => a.startTimeUtc - b.startTimeUtc);

  const layout: LayoutItem[] = [];
  // Groups of overlapping events
  const groups: CalendarEvent[][] = [];

  for (const ev of sorted) {
    let placed = false;
    for (const group of groups) {
      // Check if ev overlaps with any event in the group
      const overlaps = group.some(
        (g) => ev.startTimeUtc < g.endTimeUtc && ev.endTimeUtc > g.startTimeUtc
      );
      if (overlaps) {
        group.push(ev);
        placed = true;
        break;
      }
    }
    if (!placed) {
      groups.push([ev]);
    }
  }

  for (const group of groups) {
    const colTotal = Math.min(group.length, MAX_VISIBLE_COLS);
    const visibleCount = group.length > MAX_VISIBLE_COLS ? MAX_VISIBLE_COLS - 1 : group.length;
    const visible = group.slice(0, visibleCount);
    const hidden = group.slice(visibleCount);

    visible.forEach((ev, colIndex) => {
      const { top, height } = computeEventTimeBox(ev);
      layout.push({ kind: "event", event: ev, colIndex, colTotal, top, height });
    });

    if (hidden.length > 0) {
      const boxes = hidden.map(computeEventTimeBox);
      const top = Math.min(...boxes.map((b) => b.top));
      const bottom = Math.max(...boxes.map((b) => b.top + b.height));
      const height = Math.max(bottom - top, MIN_EVENT_HEIGHT);
      layout.push({ kind: "more", events: hidden, colIndex: colTotal - 1, colTotal, top, height });
    }
  }

  return layout;
}

/**
 * Given a list of events and a target calendar day, return events that
 * appear on that day (including multi-day events).
 */
function eventsForDay(events: CalendarEvent[], day: Date): CalendarEvent[] {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(day);
  dayEnd.setHours(23, 59, 59, 999);

  return events.filter(
    (ev) =>
      ev.startTimeUtc <= dayEnd.getTime() && ev.endTimeUtc >= dayStart.getTime()
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface EventBlockProps {
  layout: LayoutEventItem;
  dayDate: Date;
  onClick?: (event: CalendarEvent) => void;
}

function EventBlock({ layout, dayDate, onClick }: EventBlockProps) {
  const { event, colIndex, colTotal, top, height } = layout;
  const color = resolveColor(event);
  const isDraft = event.isPublished === false;
  const widthPct = 100 / colTotal;
  const leftPct = (colIndex / colTotal) * 100;

  // For multi-day events, clamp display start to this day
  const dayStart = new Date(dayDate);
  dayStart.setHours(0, 0, 0, 0);

  const startDate = new Date(
    Math.max(event.startTimeUtc, dayStart.getTime())
  );
  const timeLabel = startDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${event.title} at ${timeLabel}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(event);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(event);
        }
      }}
      className="absolute overflow-hidden cursor-pointer select-none rounded transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1"
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `calc(${leftPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
        backgroundColor: hexToRgba(color, isDraft ? 0.45 : 0.18),
        borderLeft: `3px solid ${color}`,
        border: isDraft
          ? `2px dashed ${color}`
          : `1px solid ${hexToRgba(color, 0.4)}`,
        borderLeftWidth: isDraft ? "2px" : "3px",
        opacity: isDraft ? 0.7 : 1,
      }}
    >
      <div className="px-1 py-0.5 h-full flex flex-col justify-start overflow-hidden">
        <div className="flex items-start gap-0.5 min-w-0">
          <span
            className="text-[11px] font-semibold leading-tight truncate flex-1 min-w-0"
            style={{ color }}
          >
            {event.title}
          </span>
          {event.recurrenceRule && event.recurrenceRule !== "none" && (
            <Repeat2
              size={9}
              style={{ color }}
              className="flex-shrink-0 mt-0.5 opacity-70"
              aria-label="Recurring event"
            />
          )}
          {event.joinLink && (
            <a
              href={event.joinLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex-shrink-0 mt-0.5"
              aria-label="Join link"
            >
              <ExternalLink
                size={10}
                style={{ color }}
                className="opacity-80 hover:opacity-100"
              />
            </a>
          )}
        </div>
        {height >= 22 && (
          <span
            className="text-[10px] leading-tight opacity-75 truncate"
            style={{ color }}
          >
            {timeLabel}
            {isDraft && (
              <span className="ml-1 italic opacity-60">(draft)</span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

interface MoreChipProps {
  layout: LayoutMoreItem;
  onEventClick?: (event: CalendarEvent) => void;
  /** True when this chip sits in the rightmost day column, so the popover opens to the left instead of getting clipped by the calendar's scroll container. */
  alignRight?: boolean;
}

/** Collapsed "+N more" chip for an overlap group beyond MAX_VISIBLE_COLS — click to see the rest. */
function MoreChip({ layout, onEventClick, alignRight }: MoreChipProps) {
  const { events, colIndex, colTotal, top, height } = layout;
  const [open, setOpen] = useState(false);
  const widthPct = 100 / colTotal;
  const leftPct = (colIndex / colTotal) * 100;

  return (
    <div
      className="absolute"
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `calc(${leftPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="w-full h-full rounded border border-gray-300 bg-gray-100 hover:bg-gray-200 text-[10px] font-semibold text-gray-600 flex items-center justify-center transition-colors overflow-hidden px-0.5 leading-tight"
        aria-label={`${events.length} more event${events.length === 1 ? "" : "s"}`}
      >
        +{events.length} more
      </button>
      {open && (
        <div
          role="menu"
          onMouseLeave={() => setOpen(false)}
          className={`absolute z-30 top-0 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-56 overflow-auto ${
            alignRight ? "right-full mr-1" : "left-full ml-1"
          }`}
        >
          {events.map((ev) => {
            const color = resolveColor(ev);
            const timeLabel = new Date(ev.startTimeUtc).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            });
            return (
              <button
                key={ev.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onEventClick?.(ev);
                }}
                className="w-full text-left px-2.5 py-1.5 hover:bg-gray-50 transition-colors"
              >
                <div className="text-[11px] font-semibold truncate" style={{ color }}>{ev.title}</div>
                <div className="text-[10px] text-gray-400">{timeLabel}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

function SkeletonGrid() {
  return (
    <div className="animate-pulse">
      {/* Header row */}
      <div className="flex border-b border-gray-200">
        <div className="w-14 flex-shrink-0" />
        {DAYS_OF_WEEK.map((d) => (
          <div key={d} className="flex-1 py-3 px-2">
            <div className="h-3 bg-gray-200 rounded w-8 mx-auto mb-1" />
            <div className="h-5 bg-gray-200 rounded w-6 mx-auto" />
          </div>
        ))}
      </div>
      {/* Time rows */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex border-b border-gray-100">
          <div className="w-14 flex-shrink-0 py-4 flex items-start justify-end pr-2">
            <div className="h-3 bg-gray-200 rounded w-10" />
          </div>
          {DAYS_OF_WEEK.map((d, j) => (
            <div
              key={d}
              className="flex-1 border-l border-gray-100"
              style={{ height: `${HOUR_HEIGHT}px` }}
            >
              {(i + j) % 5 === 0 && (
                <div
                  className="mx-1 mt-2 rounded bg-gray-200"
                  style={{
                    height: `${MIN_EVENT_HEIGHT}px`,
                    opacity: 0.6,
                  }}
                />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function WeeklyCalendarView({
  events,
  onEventClick,
  onSlotClick,
  isLoading = false,
}: WeeklyCalendarViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [weekStart, setWeekStart] = useState<Date>(() =>
    getMondayOfWeek(new Date())
  );

  // Scroll to 9 AM on mount so the main working window is immediately visible
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = (SCROLL_TO_HOUR - HOUR_START) * HOUR_HEIGHT;
    }
  }, []);

  // Build the 7-day array for the current week
  const weekDays = useMemo<Date[]>(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  // Hour labels for left column
  const hourLabels = useMemo<string[]>(() => {
    const labels: string[] = [];
    for (let h = HOUR_START; h <= HOUR_END; h++) {
      if (h === 0 || h === 24) {
        labels.push("12 AM");
      } else if (h === 12) {
        labels.push("12 PM");
      } else if (h < 12) {
        labels.push(`${h} AM`);
      } else {
        labels.push(`${h - 12} PM`);
      }
    }
    return labels;
  }, []);

  // Per-day layout maps (memoized)
  const dayLayouts = useMemo<Map<string, LayoutItem[]>>(() => {
    const map = new Map<string, LayoutItem[]>();
    for (const day of weekDays) {
      const key = toDateKey(day);
      const dayEvents = eventsForDay(events, day);
      map.set(key, computeLayout(dayEvents));
    }
    return map;
  }, [events, weekDays]);

  // Navigation
  function goToPrevWeek() {
    setWeekStart((prev) => addDays(prev, -7));
  }
  function goToNextWeek() {
    setWeekStart((prev) => addDays(prev, 7));
  }
  function goToToday() {
    setWeekStart(getMondayOfWeek(new Date()));
  }

  // Month/year header label
  const headerLabel = useMemo(() => {
    const startLabel = weekStart.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    const endDay = addDays(weekStart, 6);
    if (
      endDay.getMonth() !== weekStart.getMonth() ||
      endDay.getFullYear() !== weekStart.getFullYear()
    ) {
      const endLabel = endDay.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      return `${startLabel} – ${endLabel}`;
    }
    return startLabel;
  }, [weekStart]);

  // Slot click handler: compute the ms timestamp for the clicked slot
  function handleSlotClick(day: Date, hourIndex: number, e: React.MouseEvent<HTMLDivElement>) {
    if (!onSlotClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const relY = e.clientY - rect.top;
    const fractionalHours = relY / HOUR_HEIGHT;
    const totalMinutes = Math.floor(fractionalHours * 60);
    const clickedHour = HOUR_START + hourIndex + Math.floor(totalMinutes / 60);
    const clickedMin = totalMinutes % 60;

    const slotDate = new Date(day);
    slotDate.setHours(clickedHour, clickedMin, 0, 0);
    onSlotClick(slotDate.getTime());
  }

  const totalGridHeight = (HOUR_END - HOUR_START + 1) * HOUR_HEIGHT;

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* ------------------------------------------------------------------ */}
      {/* Toolbar                                                              */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Today
          </button>
          <button
            onClick={goToPrevWeek}
            aria-label="Previous week"
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft size={18} className="text-gray-600" />
          </button>
          <button
            onClick={goToNextWeek}
            aria-label="Next week"
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
          >
            <ChevronRight size={18} className="text-gray-600" />
          </button>
        </div>
        <h2 className="text-sm font-semibold text-gray-800 hidden sm:block">
          {headerLabel}
        </h2>
        {onSlotClick && (
          <button
            onClick={() => onSlotClick(Date.now())}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">New Event</span>
          </button>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Scrollable grid container                                            */}
      {/* ------------------------------------------------------------------ */}
      <p className="px-3 py-1 text-xs text-gray-600">Times shown in your device timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto"
        style={{ WebkitOverflowScrolling: "touch" } as React.CSSProperties}
      >
        {/* A 7-day grid genuinely can't shrink below a usable width on a phone
            — below md, keep a horizontal-scroll floor. From md up, let it use
            whatever width the container actually gives it instead of always
            pinning to a fixed 700px regardless of available space. */}
        <div className="min-w-[560px] md:min-w-0">
          {isLoading ? (
            <SkeletonGrid />
          ) : (
            <>
              {/* ---------------------------------------------------------- */}
              {/* Sticky day-header row                                        */}
              {/* ---------------------------------------------------------- */}
              <div className="flex sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
                {/* Time gutter */}
                <div className="w-14 flex-shrink-0" />
                {weekDays.map((day) => {
                  const today = isToday(day);
                  return (
                    <div
                      key={toDateKey(day)}
                      className="flex-1 text-center py-2 px-1"
                    >
                      <div
                        className={`text-xs font-medium uppercase tracking-wide ${
                          today ? "text-blue-600" : "text-gray-500"
                        }`}
                      >
                        {DAYS_OF_WEEK[weekDays.indexOf(day)]}
                      </div>
                      <div
                        className={`mt-0.5 mx-auto w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold ${
                          today
                            ? "bg-blue-600 text-white"
                            : "text-gray-800"
                        }`}
                      >
                        {day.getDate()}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {formatDayLabel(day).split(" ")[0]}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ---------------------------------------------------------- */}
              {/* Time grid                                                     */}
              {/* ---------------------------------------------------------- */}
              <div className="flex" style={{ height: `${totalGridHeight}px` }}>
                {/* Time labels column */}
                <div className="w-14 flex-shrink-0 relative">
                  {hourLabels.map((label, i) => (
                    <div
                      key={label}
                      className="absolute right-2 text-[10px] text-gray-400 font-medium leading-none"
                      style={{
                        top: `${i * HOUR_HEIGHT - 6}px`,
                        // Hide the very first label to avoid clipping at top
                        visibility: i === 0 ? "hidden" : "visible",
                      }}
                    >
                      {label}
                    </div>
                  ))}
                </div>

                {/* Day columns */}
                {weekDays.map((day, dayIdx) => {
                  const key = toDateKey(day);
                  const layouts = dayLayouts.get(key) ?? [];
                  const todayCol = isToday(day);
                  const isLastDayCol = dayIdx === weekDays.length - 1;

                  return (
                    <div
                      key={key}
                      className={`flex-1 relative border-l border-gray-200 ${
                        todayCol ? "bg-blue-50/40" : ""
                      }`}
                    >
                      {/* Horizontal hour lines */}
                      {hourLabels.map((label, i) => (
                        <div
                          key={label}
                          role={onSlotClick ? "button" : undefined}
                          tabIndex={onSlotClick ? 0 : undefined}
                          aria-label={
                            onSlotClick
                              ? `Add event on ${formatDayLabel(day)} at ${label}`
                              : undefined
                          }
                          className={`absolute left-0 right-0 border-t ${
                            i === 0 ? "border-transparent" : "border-gray-100"
                          } ${onSlotClick ? "cursor-pointer hover:bg-blue-50/60 transition-colors" : ""}`}
                          style={{
                            top: `${i * HOUR_HEIGHT}px`,
                            height: `${HOUR_HEIGHT}px`,
                          }}
                          onClick={
                            onSlotClick
                              ? (e) => handleSlotClick(day, i, e)
                              : undefined
                          }
                          onKeyDown={
                            onSlotClick
                              ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    const slotDate = new Date(day);
                                    slotDate.setHours(HOUR_START + i, 0, 0, 0);
                                    onSlotClick(slotDate.getTime());
                                  }
                                }
                              : undefined
                          }
                        >
                          {/* Half-hour divider */}
                          <div
                            className="absolute left-0 right-0 border-t border-gray-50"
                            style={{ top: `${HOUR_HEIGHT / 2}px` }}
                          />
                        </div>
                      ))}

                      {/* Current time indicator */}
                      {todayCol && (() => {
                        const now = new Date();
                        const nowHour =
                          now.getHours() + now.getMinutes() / 60;
                        if (nowHour >= HOUR_START && nowHour <= HOUR_END) {
                          const topPx = (nowHour - HOUR_START) * HOUR_HEIGHT;
                          return (
                            <div
                              className="absolute left-0 right-0 z-10 pointer-events-none"
                              style={{ top: `${topPx}px` }}
                            >
                              <div className="flex items-center">
                                <div className="w-2 h-2 rounded-full bg-red-500 -ml-1 flex-shrink-0" />
                                <div className="flex-1 border-t-2 border-red-500" />
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Events */}
                      {layouts.map((item) =>
                        item.kind === "event" ? (
                          <EventBlock
                            key={`${item.event.id}-${item.colIndex}`}
                            layout={item}
                            dayDate={day}
                            onClick={onEventClick}
                          />
                        ) : (
                          <MoreChip
                            key={`more-${item.colIndex}-${item.top}`}
                            layout={item}
                            onEventClick={onEventClick}
                            alignRight={isLastDayCol}
                          />
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
